import { createRouter, createWebHistory } from 'vue-router';
import { useUserStore } from '@/stores/user';
import { usePermissionsStore } from '@/stores/permissions';
import { useProjectStore } from '@/stores/project';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { left: 0, top: 0 };
    }
  },
  routes: [
    {
      path: '/',
      redirect: '/projects',
    },
    {
      path: '/login',
      name: 'Login',
      component: () => import('@/views/login/Login.vue'),
      beforeEnter: (to, from, next) => {
        const userStore = useUserStore();
        const projectStore = useProjectStore();
        if (userStore.isAuthenticated) {
          next({
            name: 'Dashboard',
            params: { projectId: projectStore.currentProjectId },
          });
        }
        next();
      },
    },
    {
      path: '/projects',
      name: 'Projects',
      component: () => import('@/views/projects/Projects.vue'),
      meta: { requiresAuth: true },
    },

    {
      path: '/profile',
      name: 'Profile',
      component: () => import('@/views/profile/Profile.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/profile/:id',
      name: 'MemberProfile',
      component: () => import('@/views/profile/Profile.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/:projectId',
      children: [
        {
          path: 'dashboard',
          name: 'Dashboard',
          component: () => import('@/views/dashboard/Dashboard.vue'),
          meta: { requiresAuth: true, requiredPermission: 'VIEW_DASHBOARD' },
        },
        {
          path: 'actions/discover',
          name: 'DiscoverActions',
          component: () => import('@/views/actions/Discover.vue'),
          meta: {
            requiresAuth: true,
            requiredPermission: 'VIEW_ACTION_JOBS',
            mutableProjectId: true,
          },
        },
        {
          path: 'actions/jobs',
          name: 'ActionJobsList',
          component: () => import('@/views/actions/Jobs.vue'),
          meta: {
            requiresAuth: true,
            requiredPermission: 'VIEW_ACTION_JOBS',
          },
        },
        {
          path: 'actions/jobs/:jobId',
          name: 'ActionJobDetail',
          component: () => import('@/views/actions/Job.vue'),
          meta: {
            requiresAuth: true,
            hideTabBar: true,
            requiredPermission: 'VIEW_ACTION_JOBS',
          },
        },
        {
          path: 'actions',
          name: 'Actions',
          component: () => import('@/views/actions/Actions.vue'),
          meta: {
            requiresAuth: true,
            requiredPermission: 'VIEW_ACTION_JOBS',
            mutableProjectId: true,
          },
        },
        {
          path: 'actions/:actionId',
          name: 'ActionDetail',
          component: () => import('@/views/actions/Action.vue'),
          meta: {
            requiresAuth: true,
            hideTabBar: true,
            requiredPermission: 'VIEW_ACTION_JOBS',
            mutableProjectId: true,
          },
        },
        {
          path: 'distributions',
          name: 'Distributions',
          component: () => import('@/views/distributions/Distributions.vue'),
          meta: {
            requiresAuth: true,
            requiredPermission: 'VIEW_DISTRIBUTIONS',
          },
        },
        {
          path: 'distributions/:id',
          name: 'Distribution',
          component: () =>
            import('@/views/distributions/distribution/Index.vue'),
          meta: {
            requiresAuth: true,
            requiredPermission: 'VIEW_DISTRIBUTIONS',
          },
          redirect: (to) =>
            `/${to.params.projectId}/distributions/${to.params.id}/general`,
          children: [
            {
              path: 'general',
              name: 'DistributionGeneral',
              component: () =>
                import('@/views/distributions/distribution/General.vue'),
              meta: {
                requiresAuth: true,
                requiredPermission: 'VIEW_DISTRIBUTIONS',
              },
            },
            {
              path: 'origins',
              name: 'DistributionOrigins',
              component: () =>
                import('@/views/distributions/distribution/Origins.vue'),
              meta: {
                requiresAuth: true,
                requiredPermission: 'VIEW_DISTRIBUTIONS',
              },
            },
            {
              path: 'behaviors',
              name: 'DistributionBehaviors',
              component: () =>
                import('@/views/distributions/distribution/Behaviors.vue'),
              meta: {
                requiresAuth: true,
                requiredPermission: 'VIEW_DISTRIBUTIONS',
              },
            },
            {
              path: 'error-responses',
              name: 'DistributionErrorResponses',
              component: () =>
                import('@/views/distributions/distribution/ErrorResponses.vue'),
              meta: {
                requiresAuth: true,
                requiredPermission: 'VIEW_DISTRIBUTIONS',
              },
            },
            {
              path: 'invalidations',
              name: 'DistributionInvalidations',
              component: () =>
                import('@/views/distributions/distribution/Invalidations.vue'),
              meta: {
                requiresAuth: true,
                requiredPermission: 'VIEW_DISTRIBUTIONS',
              },
            },
            {
              path: 'invalidations/:invalidationId',
              name: 'DistributionInvalidationDetail',
              component: () =>
                import('@/views/distributions/distribution/Invalidation.vue'),
              meta: {
                requiresAuth: true,
                requiredPermission: 'VIEW_DISTRIBUTIONS',
                hideTabBar: true,
              },
            },
            {
              path: 'tags',
              name: 'DistributionTags',
              component: () =>
                import('@/views/distributions/distribution/Tags.vue'),
              meta: {
                requiresAuth: true,
                requiredPermission: 'VIEW_DISTRIBUTIONS',
              },
            },
          ],
        },
        {
          path: 'certificates',
          name: 'Certificates',
          component: () => import('@/views/certificates/Certificates.vue'),
          meta: { requiresAuth: true, requiredPermission: 'VIEW_CERTIFICATES' },
        },
        {
          path: 'certificates/:id',
          name: 'Certificate',
          component: () => import('@/views/certificates/Certificate.vue'),
          meta: { requiresAuth: true, requiredPermission: 'VIEW_CERTIFICATES' },
        },
        {
          path: 'libraries',
          name: 'Libraries',
          component: () => import('@/views/libraries/Libraries.vue'),
          meta: { requiresAuth: true, requiredPermission: 'VIEW_LIBRARIES' },
        },
        {
          path: 'libraries/:id',
          name: 'Library',
          component: () => import('@/views/libraries/library/Index.vue'),
          meta: { requiresAuth: true, requiredPermission: 'VIEW_LIBRARIES' },
          redirect: (to) =>
            `/${to.params.projectId}/libraries/${to.params.id}/overview`,
          children: [
            {
              path: 'overview',
              name: 'LibraryOverview',
              component: () => import('@/views/libraries/library/Overview.vue'),
              meta: {
                requiresAuth: true,
                requiredPermission: 'VIEW_LIBRARIES',
              },
            },
            {
              path: 'configuration',
              name: 'LibraryConfiguration',
              component: () =>
                import('@/views/libraries/library/Configuration.vue'),
              meta: {
                requiresAuth: true,
                requiredPermission: 'VIEW_LIBRARIES',
              },
            },
            {
              path: ':assetId',
              name: 'Asset',
              component: () => import('@/views/libraries/Asset.vue'),
              meta: {
                requiresAuth: true,
                requiredPermission: 'VIEW_LIBRARIES',
                hideTabBar: true,
              },
            },
          ],
        },

        {
          path: 'keys',
          name: 'Keys',
          component: () => import('@/views/keys/Keys.vue'),
          meta: { requiresAuth: true, requiredPermission: 'VIEW_KEYS' },
        },
        {
          path: 'team',
          name: 'Team',
          component: () => import('@/views/team/Team.vue'),
          meta: { requiresAuth: true, requiredPermission: 'VIEW_TEAM_MEMBERS' },
        },
        {
          path: 'invoices',
          name: 'Invoices',
          component: () => import('@/views/billing/Invoices.vue'),
          meta: { requiresAuth: true, requiredPermission: 'VIEW_INVOICES' },
        },
        {
          path: 'invoices/:id',
          name: 'Invoice',
          component: () => import('@/views/billing/Invoice.vue'),
          meta: { requiresAuth: true, requiredPermission: 'VIEW_INVOICES' },
        },
        {
          path: 'billing-details',
          name: 'BillingDetails',
          component: () => import('@/views/billing/BillingDetails.vue'),
          meta: { requiresAuth: true, requiredPermission: 'VIEW_BILLING_INFO' },
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'error',
      component: () => import('@/views/ErrorView.vue'),
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore();
  const permissionsStore = usePermissionsStore();
  const projectStore = useProjectStore();

  if (to.meta.requiresAuth) {
    // Check if the user is authenticated
    if (!localStorage.getItem('access_token') && !userStore.isAuthenticated) {
      localStorage.setItem('redirectAfterLogin', to.fullPath);
      next({ name: 'Login' });
      return;
    }
  }

  // Check if route contains projectId and validate it exists
  if (to.params.projectId && to.meta.mutableProjectId) {
    const projectId = to.params.projectId;

    // Check if the project exists in the store
    const projectExists = projectStore.projects.some(
      (project) => project.project_id == projectId
    );

    // If project doesn't exist and we have at least one project, redirect to the first project
    if (!projectExists && projectStore.projects.length > 0) {
      const defaultProjectId =
        projectStore.savedProjectId || projectStore.projects[0].project_id;
      const redirectPath = to.fullPath.replace(projectId, defaultProjectId);
      next(redirectPath);
      return;
    }
  }

  if (to.meta.requiredPermission) {
    const requiredPermission = to.meta.requiredPermission;
    if (!permissionsStore.hasPermission(requiredPermission)) {
      next({ path: '/page-not-found' });
      return;
    }
  }

  // Proceed with the navigation
  next();
});

router.afterEach((to, from) => {
  if (to.name !== 'Login') {
    localStorage.removeItem('redirectAfterLogin');
  }
});

export default router;
